import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  message,
  Popconfirm,
  Descriptions,
  Spin,
} from 'antd';
import {
  DollarOutlined,
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { currencySettingsApi, show422Error } from '../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';

const CURRENCY_POSITION_OPTIONS = [
  { value: 'left', label: 'چپ' },
  { value: 'right', label: 'راست' },
  { value: 'left_with_space', label: 'چپ با فاصله' },
  { value: 'right_with_space', label: 'راست با فاصله' },
];

const CRYPTO_OPTIONS = [
  { value: 'no', label: 'خیر' },
  { value: 'yes', label: 'بله' },
];

const positionLabel = (v) => CURRENCY_POSITION_OPTIONS.find((o) => o.value === v)?.label || v || '—';

const CurrencySettings = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [formOpen, setFormOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewRow, setViewRow] = useState(null);

  const extractList = (res) => res?.currencies || res?.data?.currencies || [];

  const fetchIndex = useCallback(async () => {
    setLoading(true);
    try {
      const res = await currencySettingsApi.getAll();
      setRows(extractList(res) || []);
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت فهرست ارزها');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIndex();
  }, [fetchIndex]);

  const buildPayload = (values) => ({
    currency_name: String(values.currency_name || '').trim(),
    currency_symbol: String(values.currency_symbol || '').trim(),
    currency_code: String(values.currency_code || '').trim(),
    is_cryptocurrency: values.is_cryptocurrency,
    exchange_rate: values.exchange_rate != null ? Number(values.exchange_rate) : 0,
    currency_position: values.currency_position,
    no_of_decimal: values.no_of_decimal != null ? Number(values.no_of_decimal) : 0,
    thousand_separator: String(values.thousand_separator ?? ','),
    decimal_separator: String(values.decimal_separator ?? '.'),
  });

  const openCreate = () => {
    setEditingId(null);
    setFormOpen(true);
    setFormLoading(false);
    form.resetFields();
    form.setFieldsValue({
      is_cryptocurrency: 'no',
      exchange_rate: 1,
      no_of_decimal: 0,
      thousand_separator: ',',
      decimal_separator: '.',
      currency_position: 'right_with_space',
    });
  };

  const openEdit = async (id) => {
    setEditingId(id);
    setFormOpen(true);
    setFormLoading(true);
    form.resetFields();
    try {
      const res = await currencySettingsApi.getById(id);
      const c = res?.data?.currency || res?.currency;
      if (!c) {
        message.warning('رکورد یافت نشد');
        setFormOpen(false);
        return;
      }
      form.setFieldsValue({
        currency_name: c.currency_name,
        currency_symbol: c.currency_symbol,
        currency_code: c.currency_code,
        is_cryptocurrency: c.is_cryptocurrency || 'no',
        exchange_rate: c.exchange_rate,
        currency_position: c.currency_position,
        no_of_decimal: c.no_of_decimal,
        thousand_separator: c.thousand_separator ?? ',',
        decimal_separator: c.decimal_separator ?? '.',
      });
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت اطلاعات ارز');
      setFormOpen(false);
    } finally {
      setFormLoading(false);
    }
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = buildPayload(values);
      setFormSubmitting(true);
      if (editingId) {
        await currencySettingsApi.update(editingId, payload);
        message.success('ارز بروزرسانی شد');
      } else {
        await currencySettingsApi.create(payload);
        message.success('ارز جدید ثبت شد');
      }
      setFormOpen(false);
      fetchIndex();
    } catch (error) {
      if (error?.errorFields) return;
      if (error?.name === 'ValidationError' || error?.details) show422Error(error, form);
      else message.error(error?.message || 'خطا در ذخیره');
    } finally {
      setFormSubmitting(false);
    }
  };

  const onDelete = async (id) => {
    try {
      await currencySettingsApi.delete(id);
      message.success('ارز حذف شد');
      fetchIndex();
    } catch (error) {
      message.error(error?.message || 'خطا در حذف');
    }
  };

  const openView = async (id) => {
    setViewOpen(true);
    setViewLoading(true);
    setViewRow(null);
    try {
      const res = await currencySettingsApi.getById(id);
      const c = res?.data?.currency || res?.currency;
      setViewRow(c || null);
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت جزئیات');
      setViewOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const columns = [
    { title: 'نام', dataIndex: 'currency_name', key: 'currency_name', ellipsis: true },
    { title: 'نماد', dataIndex: 'currency_symbol', key: 'currency_symbol', width: 90 },
    { title: 'کد', dataIndex: 'currency_code', key: 'currency_code', width: 100 },
    {
      title: 'نرخ برابری',
      dataIndex: 'exchange_rate',
      key: 'exchange_rate',
      width: 110,
      render: (v) => (v != null ? String(v) : '—'),
    },
    {
      title: 'رمزارز',
      dataIndex: 'is_cryptocurrency',
      key: 'crypto',
      width: 90,
      render: (v) => (
        <Tag color={v === 'yes' ? 'purple' : 'default'}>{v === 'yes' ? 'بله' : 'خیر'}</Tag>
      ),
    },
    {
      title: 'موقعیت نمایش',
      dataIndex: 'currency_position',
      key: 'currency_position',
      ellipsis: true,
      render: (v) => positionLabel(v),
    },
    {
      title: 'اعشار',
      dataIndex: 'no_of_decimal',
      key: 'no_of_decimal',
      width: 72,
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 168,
      fixed: 'right',
      render: (_, row) => (
        <Space size="small" wrap>
          <Button type="text" icon={<EyeOutlined />} aria-label="مشاهده" onClick={() => openView(row.id)} />
          <Button type="text" icon={<EditOutlined />} aria-label="ویرایش" onClick={() => openEdit(row.id)} />
          <Popconfirm
            title="حذف این ارز؟"
            okText="حذف"
            cancelText="لغو"
            onConfirm={() => onDelete(row.id)}
            okButtonProps={{ danger: true }}
          >
            <Button type="text" danger icon={<DeleteOutlined />} aria-label="حذف" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="custom-font">
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-700/70">
                <DollarOutlined className="text-xl text-gray-700 dark:text-gray-300" />
              </div>
              <h1 className="m-0 text-2xl font-bold text-gray-800 dark:text-gray-100">تنظیمات ارز</h1>
            </div>
            <Space wrap>
              <Button icon={<ReloadOutlined />} onClick={fetchIndex} loading={loading}>
                بروزرسانی
              </Button>
              <Button type="primary" icon={<PlusOutlined />} className={PANEL_PRIMARY_BTN_CLASS} onClick={openCreate}>
                ارز جدید
              </Button>
            </Space>
          </div>

          <div className="rounded-b-xl bg-gray-50 p-6 dark:bg-gray-900/50">
            <Card className="border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <Table
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={rows}
                pagination={false}
                scroll={{ x: 960 }}
              />
            </Card>
          </div>
        </Card>

        <Modal
          title={editingId ? 'ویرایش ارز' : 'افزودن ارز'}
          open={formOpen}
          onCancel={() => setFormOpen(false)}
          onOk={onSubmit}
          okText="ذخیره"
          cancelText="لغو"
          confirmLoading={formSubmitting}
          okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
          destroyOnClose
          width={640}
        >
          <Form form={form} layout="vertical" className="custom-font mt-2" disabled={formLoading}>
            <Spin spinning={formLoading}>
              <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
                <Form.Item name="currency_name" label="نام ارز" rules={[{ required: true, message: 'نام را وارد کنید' }]}>
                  <Input placeholder="مثال: تومان" />
                </Form.Item>
                <Form.Item name="currency_symbol" label="نماد" rules={[{ required: true, message: 'نماد را وارد کنید' }]}>
                  <Input placeholder="مثال: تومان" />
                </Form.Item>
                <Form.Item name="currency_code" label="کد ارز" rules={[{ required: true, message: 'کد را وارد کنید' }]}>
                  <Input placeholder="IRR، EUR، …" />
                </Form.Item>
                <Form.Item name="is_cryptocurrency" label="رمزارز" rules={[{ required: true }]}>
                  <Select options={CRYPTO_OPTIONS} popupClassName="custom-font" />
                </Form.Item>
                <Form.Item name="exchange_rate" label="نرخ برابری" rules={[{ required: true, message: 'نرخ را وارد کنید' }]}>
                  <InputNumber min={0} step={0.000001} className="w-full" />
                </Form.Item>
                <Form.Item name="currency_position" label="موقعیت نمایش مبلغ" rules={[{ required: true }]}>
                  <Select options={CURRENCY_POSITION_OPTIONS} popupClassName="custom-font" />
                </Form.Item>
                <Form.Item name="no_of_decimal" label="تعداد رقم اعشار" rules={[{ required: true }]}>
                  <InputNumber min={0} max={10} className="w-full" />
                </Form.Item>
                <Form.Item name="thousand_separator" label="جداکننده هزارگان" rules={[{ required: true }]}>
                  <Input maxLength={4} placeholder="," />
                </Form.Item>
                <Form.Item name="decimal_separator" label="جداکننده اعشار" rules={[{ required: true }]}>
                  <Input maxLength={4} placeholder="." />
                </Form.Item>
              </div>
            </Spin>
          </Form>
        </Modal>

        <Modal
          title="جزئیات ارز"
          open={viewOpen}
          onCancel={() => {
            setViewOpen(false);
            setViewRow(null);
          }}
          footer={
            <Button type="primary" className={PANEL_PRIMARY_BTN_CLASS} onClick={() => setViewOpen(false)}>
              بستن
            </Button>
          }
          destroyOnClose
          width={560}
        >
          {viewLoading ? (
            <div className="flex justify-center py-12">
              <Spin />
            </div>
          ) : viewRow ? (
            <Descriptions bordered column={1} size="small" className="custom-font mt-2">
              <Descriptions.Item label="نام">{viewRow.currency_name ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="نماد">{viewRow.currency_symbol ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="کد">{viewRow.currency_code ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="نرخ برابری">{viewRow.exchange_rate ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="رمزارز">{viewRow.is_cryptocurrency === 'yes' ? 'بله' : 'خیر'}</Descriptions.Item>
              <Descriptions.Item label="موقعیت نمایش">{positionLabel(viewRow.currency_position)}</Descriptions.Item>
              <Descriptions.Item label="تعداد اعشار">{viewRow.no_of_decimal ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="جداکننده هزارگان">{viewRow.thousand_separator ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="جداکننده اعشار">{viewRow.decimal_separator ?? '—'}</Descriptions.Item>
              {viewRow.usd_price != null && viewRow.usd_price !== '' ? (
                <Descriptions.Item label="قیمت دلاری">{String(viewRow.usd_price)}</Descriptions.Item>
              ) : null}
            </Descriptions>
          ) : (
            <p className="text-center text-slate-500">داده‌ای نیست</p>
          )}
        </Modal>
      </div>
    </MainLayout>
  );
};

export default CurrencySettings;
